import React, { useCallback, useState } from 'react';
import { withRouter } from 'react-router';
import { Col, Row } from 'reactstrap';
import { get } from 'lodash-es';
import axios from 'axios';

import Step4Dropzone from '../Step4/Step4Dropzone';
import imageUploadErrors from '../Step4/imageUploadErrors';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import { fileDeleteErrorText, offlineErrorText } from '../../../utilities/StaticText';
import AddLotImageContainer from './AddLotImageContainer';

const AddLotDropzone = ({ houseID, lotData, lotImages: initialLotImages, match }) => {
  const {
    params: { catalogID, lotNumber, type }
  } = match;

  const [files, setFiles] = useState([]);
  const [lotImages, setLotImages] = useState(initialLotImages);
  const [fileDeleteError, setFileDeleteError] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [offlineError, setOfflineError] = useState(false);

  const abortFile = useCallback(index => {
    setFiles(prev => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }, []);

  // Track errors here so we can display them above the list of files.
  const addFileError = useCallback(
    (code, index) => {
      setFiles(prev => {
        if (!prev[index]) {
          return prev;
        }
        const next = [...prev];
        next[index].isComplete = true;
        next[index].hasError = true;
        next[index].error = imageUploadErrors(code, type);
        return next;
      });
    },
    [type]
  );

  const handleDeleteFile = useCallback(
    async index => {
      // lotNumber in props may contain the extension
      const { lotID } = lotData;
      const lotImageKeys = Object.keys(lotImages);

      if (lotImageKeys.indexOf(index.toString()) < 0) {
        return;
      }

      try {
        const postData = { catalogID, lotID, sequence: index, imgType: 'lot' };

        setIsDeletingFile(true);
        await axios.post('/cps/app/image/delete', postData, {
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
        });

        // If the delete worked - re-sequence and re-fetch the images
        await axios(`/cps/app/catalog/${catalogID}/${lotID}/resequenceimages`);
        const lotImageResponse = await axios(
          `/cps/app/catalog/${catalogID}/${lotID}/house/${houseID}/getimageurls`
        );
        const nextLotImages = get(lotImageResponse, `data.data[${lotID}]`, []);

        // Re-render!
        setLotImages(nextLotImages);
        setIsDeletingFile(false);
      } catch {
        setFileDeleteError(true);
        setIsDeletingFile(false);
      }
    },
    [catalogID, houseID, lotData, lotImages]
  );

  /**
   * Unlike step4 - when a file is complete - we add it to the lotImages JSON map which represents
   * files that have already been uploaded.
   *
   * This removes the completed file from the files array (matched by its stable seqNumber, so the
   * right file is removed regardless of list position) and updates lotImages.
   */
  const handleFileComplete = useCallback((imageURL, imageSeq) => {
    setFiles(prev => {
      const next = [...prev];
      const fileIndex = next.findIndex(file => file.seqNumber === imageSeq);
      next.splice(fileIndex, 1);
      return next;
    });
    setLotImages(prev => ({ ...prev, [imageSeq]: imageURL }));
  }, []);

  /**
   * Trigger: The user swaps two images. Images must already be uploaded and without errors.
   *
   * Effect: Swap the image the user dragged into the new location. Shift all images in between over
   * by one, persist to CPS, then commit the reordered map to state.
   */
  const onSortEnd = useCallback(
    async ({ oldIndex, newIndex }) => {
      // User drag/dropped in the same spot, do nothing
      if (oldIndex === newIndex) {
        return;
      }

      const lotImageCopy = { ...lotImages };

      const smallerNumber = Math.min(oldIndex, newIndex);
      const largerNumber = Math.max(oldIndex, newIndex);

      const postBody = { imageOrder: {} };
      // Initialize the post body with the original values
      Object.keys(lotImageCopy).forEach(key => {
        postBody.imageOrder[key] = key;
      });

      // User dragged from right to left, slidddeee to the right
      if (newIndex < oldIndex) {
        const originalVal = lotImageCopy[largerNumber];
        for (let i = largerNumber; i > smallerNumber; i--) {
          lotImageCopy[i] = lotImageCopy[i - 1];
          postBody.imageOrder[i] = i - 1;
        }
        lotImageCopy[smallerNumber] = originalVal;
        postBody.imageOrder[smallerNumber] = largerNumber;
      } else {
        // User dragged from left to right, slidddeee to the left
        const originalVal = lotImageCopy[smallerNumber];
        for (let i = smallerNumber; i < largerNumber; i++) {
          lotImageCopy[i] = lotImageCopy[i + 1];
          postBody.imageOrder[i] = i + 1;
        }
        lotImageCopy[largerNumber] = originalVal;
        postBody.imageOrder[largerNumber] = smallerNumber;
      }

      try {
        await axios.post(`/cps/app/catalog/${catalogID}/${lotNumber}/reorderimages`, postBody, {
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
        });
        // set state to new image order
        setLotImages(lotImageCopy);
      } catch (err) {
        console.log('error resequencing images'); // eslint-disable-line no-console
        console.log(err); // eslint-disable-line no-console
      }
    },
    [catalogID, lotNumber, lotImages]
  );

  /**
   * Triggers when a user drops or selects files on the dropzone.
   * In step4 we handle this differently due to lot # / inventory # constraints.
   */
  const handleFileDrop = useCallback(
    droppedFiles => {
      /**
       * Verify the user is connected to the internet before uploading files to mitigate
       * connection and timeout issues.
       */
      if (!navigator.onLine) {
        setOfflineError(true);
        return;
      }

      setFiles(existingFiles => {
        // Assign a sequence number - which allows the splice in handleFileComplete.
        let index = 1;
        /**
         * ESLint thinks file is undefined - but for loop is preferred here
         * to be consistent with Step4.js
         */
        // eslint-disable-next-line
        for (const file of droppedFiles) {
          file.error = {};
          file.hasError = false;
          file.isComplete = false;
          file.isSelected = false;
          file.photoID = null;
          file.seqNumber = Object.keys(initialLotImages).length + existingFiles.length + index;
          index += 1;
        }
        return [...existingFiles, ...droppedFiles];
      });
      setOfflineError(false);
    },
    [initialLotImages]
  );

  const handleFileDropRejected = useCallback(
    rejectedFiles => {
      /**
       * ESLint thinks file is undefined - but for loop is preferred here
       * to be consistent with Step4.js
       */
      // eslint-disable-next-line
      for (const file of rejectedFiles) {
        file.error = imageUploadErrors('type', type);
        file.isComplete = true;
        file.isSelected = false;
        file.hasError = true;
      }
      setFiles(existingFiles => [...existingFiles, ...rejectedFiles]);
    },
    [type]
  );

  return (
    <div className="mt-4">
      <Row>
        {/* Start left column */}
        <Col xs={8}>
          <strong>Upload Images</strong>
          <p className="mb-2">
            1. Drag &amp; drop images from your computer in the uploader box below (or click the
            &quot;choose a file(s) from your computer&quot; link to find your file)
          </p>
          <p className="mb-2">2. Correct Errors if shown.</p>
          <p>
            Note: any images that contain errors will not be uploaded or saved after clicking
            &quot;Save &amp; Return to Summary&quot;. To reorder, drag and drop images into place.
          </p>

          {offlineError && (
            <div className="mt-3">
              <InlineAlert baseText={offlineErrorText} showPhones={false} />
            </div>
          )}

          <Step4Dropzone
            acceptedFileList=".jpg,.jpeg,.png"
            handleFileDrop={handleFileDrop}
            handleFileDropRejected={handleFileDropRejected}
          />
        </Col>
        {/* Start right column */}
        <Col xs={4}>
          <strong>Image Requirements</strong>
          <p>Images must end in .JPG, .JPEG &amp; .PNG (Do NOT use .GIF)</p>

          <Row>
            <Col>
              <strong>Max. Size</strong>
            </Col>
          </Row>

          <Row>
            <Col>
              <p>up to 10MB</p>
            </Col>
          </Row>
        </Col>
      </Row>

      {fileDeleteError && (
        <div className="mt-3">
          <InlineAlert baseText={fileDeleteErrorText} />
        </div>
      )}

      <AddLotImageContainer
        abortFile={abortFile}
        addFileError={addFileError}
        catalogID={catalogID}
        files={files}
        handleFileComplete={handleFileComplete}
        handleDeleteFile={handleDeleteFile}
        isDeletingFile={isDeletingFile}
        lotImages={lotImages}
        lotNumber={lotNumber}
        lotData={lotData}
        onSortEnd={onSortEnd}
      />
    </div>
  );
};

export default withRouter(AddLotDropzone);
